import { lazy, Suspense, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { MarketingNav } from '@/components/landing/MarketingNav';
import { HeroSection } from '@/components/landing/HeroSection';
import { FeaturesShowcase } from '@/components/landing/FeaturesShowcase';
import { useFAQData } from '@/components/landing/FAQSection';
import { SEOHead } from '@/components/SEOHead';
import { SchemaMarkup, organizationSchema, webSiteSchema, softwareAppSchema, generateFAQSchema } from '@/components/SchemaMarkup';

// Lazy-load below-the-fold sections to reduce initial bundle and improve FCP
const SocialProofBar = lazy(() => import('@/components/landing/SocialProofBar').then(m => ({ default: m.SocialProofBar })));
const WhatIsRevOpsSection = lazy(() => import('@/components/landing/WhatIsRevOpsSection').then(m => ({ default: m.WhatIsRevOpsSection })));
const PainMirrorSection = lazy(() => import('@/components/landing/PainMirrorSection').then(m => ({ default: m.PainMirrorSection })));
const SolutionPillarsSection = lazy(() => import('@/components/landing/SolutionPillarsSection').then(m => ({ default: m.SolutionPillarsSection })));
const PersonaSection = lazy(() => import('@/components/landing/PersonaSection').then(m => ({ default: m.PersonaSection })));
const NewsletterSection = lazy(() => import('@/components/landing/NewsletterSection').then(m => ({ default: m.NewsletterSection })));
const LandingPricingSection = lazy(() => import('@/components/landing/LandingPricingSection').then(m => ({ default: m.LandingPricingSection })));
const TestimonialsSection = lazy(() => import('@/components/landing/TestimonialsSection').then(m => ({ default: m.TestimonialsSection })));
const FinalCTASection = lazy(() => import('@/components/landing/FinalCTASection').then(m => ({ default: m.FinalCTASection })));
const FAQSection = lazy(() => import('@/components/landing/FAQSection').then(m => ({ default: m.FAQSection })));
const LandingFooter = lazy(() => import('@/components/landing/LandingFooter').then(m => ({ default: m.LandingFooter })));
const ExitIntentPopup = lazy(() => import('@/components/landing/ExitIntentPopup').then(m => ({ default: m.ExitIntentPopup })));

export default function LandingPage() {
  const { i18n } = useTranslation();
  const isPt = i18n.language?.startsWith('pt');
  const faqData = useFAQData();
  const { isAuthenticated, organizations } = useAuth();
  const navigate = useNavigate();

  // Authenticated users can still browse the landing page freely.
  // They enter the app via the "Entrar" / "Sign in" button in MarketingNav.

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title={isPt ? "RevOps AI — Revenue Intelligence para HubSpot e RD Station" : "RevOps AI — Revenue Intelligence for HubSpot & RD Station"}
        description={isPt ? "Transforme os dados do seu CRM em receita previsível. Diagnósticos de funil, forecast matemático e ações concretas. Teste grátis por 7 dias." : "Transform your CRM data into predictable revenue. Funnel diagnostics, mathematical forecasting, and actionable insights. Free 7-day trial."}
        path="/"
        keywords={isPt ? "RevOps, Revenue Intelligence, CRM, HubSpot, RD Station, forecast, pipeline" : "RevOps, Revenue Intelligence, CRM, HubSpot, RD Station, forecast, pipeline"}
      />
      <SchemaMarkup schema={[organizationSchema, webSiteSchema, softwareAppSchema, generateFAQSchema(faqData)]} />
      <MarketingNav />
      <main id="main-content">
        <HeroSection />
        <FeaturesShowcase />
        <Suspense fallback={null}>
          <SocialProofBar />
          <WhatIsRevOpsSection />
          <PainMirrorSection />
          <SolutionPillarsSection />
          <PersonaSection />
          <NewsletterSection />
          <LandingPricingSection />
          <FAQSection />
          <TestimonialsSection />
          <FinalCTASection />
        </Suspense>
      </main>
      <Suspense fallback={null}>
        <LandingFooter />
        <ExitIntentPopup />
      </Suspense>
    </div>
  );
}
